import { AnyAction } from 'redux';
import { GlobalActions } from '@ps20-frontend/shop-integration-redux';
import { App, Immobilie, VhvVorbelegungdaten } from '@ps20-frontend/frontend-models';
import { GebaeudeArtKdv, HausArt, Stockwerk } from '@ps20/bff-models';
import { VhvAction, VhvActions } from '../vhvActions';
import { IAppState } from '../appState';

export interface VersObjekt {
  wohnflaeche: number;
  hausart: HausArt;
  staendigBewohnt: boolean;
  gebaeudeStaendigBewohnt: boolean;
  gebaeudeInnerhalbWohngebiet: boolean;
  stockwerk: Stockwerk;
  plzFaktorErdb?: number;
  plzFaktorGla?: number;
  plzFaktorHws?: number;
  selbstbehaltUeberschwemmung?: number;
}

const mapKdvArtToHausart = function (gebaeudeArtKdv: GebaeudeArtKdv): HausArt {
  return ({
    [GebaeudeArtKdv.KDV_IMMOART_EINFAMILIENHAUS]: HausArt.EINFAMILIENHAUS,
    [GebaeudeArtKdv.KDV_IMMOART_FERIENHAUS]: HausArt.EINFAMILIENHAUS,
    [GebaeudeArtKdv.KDV_IMMOART_FERIENWOHNUNG]: HausArt.MEHRFAMILIENHAUS,
    [GebaeudeArtKdv.KDV_IMMOART_EIGENTUMSWOHNUNG]: HausArt.MEHRFAMILIENHAUS,
    [GebaeudeArtKdv.KDV_IMMOART_WHG_IN_MEHRFAMHAUS]: HausArt.MEHRFAMILIENHAUS,
  } as Partial<Record<GebaeudeArtKdv, HausArt>>)[gebaeudeArtKdv];
};

function mapStockwerk(immobilie: Immobilie): Stockwerk {
  return mapKdvArtToHausart(immobilie.gebaeudeArtKdv) === HausArt.MEHRFAMILIENHAUS
    ? immobilie.stockwerk
    : null;
}

export const versObjektReducer = function (
  sectionState: VersObjekt = null,
  action: AnyAction,
  appState: IAppState,
): VersObjekt {
  switch (action.type) {
    case GlobalActions.IMMOBILIE_UEBERNEHMEN: {
      const immobilie = action.payload.immobilie as Immobilie;
      if (
        action.payload.appId !== App.VHV_V2 ||
        action.payload.angebotsId !== appState.angebotsId ||
        !immobilie
      ) {
        return sectionState;
      }

      const versObjekt: VersObjekt = {
        ...sectionState,
        wohnflaeche: immobilie.wohnflaeche,
        hausart: immobilie.hausart
          ? immobilie.hausart
          : mapKdvArtToHausart(immobilie.gebaeudeArtKdv),
        stockwerk:
          immobilie.stockwerk && immobilie.hausart === HausArt.MEHRFAMILIENHAUS
            ? immobilie.stockwerk
            : mapStockwerk(immobilie),
      };

      if (appState.vertragsoptionen?.vorvertragsNummern?.length > 0) {
        versObjekt.staendigBewohnt = immobilie.staendigBewohnt;
      }
      return versObjekt;
    }
    case VhvActions.VERSICHERUNGS_OBJEKT_CHANGED: {
      const objectChangedAction: VhvAction<Partial<VersObjekt>> = action;
      const changedAttributes = objectChangedAction.payload;
      return { ...sectionState, ...changedAttributes };
    }
    case VhvActions.GEBAEUDE_STAENDIG_BEWOHNT_CHANGED: {
      return {
        ...sectionState,
        gebaeudeStaendigBewohnt: action.payload.gebaeudeStaendigBewohnt,
      };
    }
    case VhvActions.WOHNFLAECHE_CHANGED: {
      return {
        ...sectionState,
        wohnflaeche: action.payload.wohnflaeche,
      };
    }
    case VhvActions.HAUSART_CHANGED: {
      return {
        ...sectionState,
        hausart: action.payload.hausart, stockwerk: null,
      };
    }
    case VhvActions.GEBAEUDE_INNERHALB_WOHNGEBIET_CHANGED: {
      return {
        ...sectionState,
        gebaeudeInnerhalbWohngebiet: action.payload.innerhalbWohngebiet,
      };
    }
    case VhvActions.STOCKWERK_CHANGED: {
      return { ...sectionState, stockwerk: action.payload.stockwerk };
    }
    case GlobalActions.VORBELEGUNG_FOR_BEIBEHALTUNG_REQUESTED: {
      const actionPayload: VhvVorbelegungdaten = action.payload;
      if (actionPayload.forApp === App.VHV_V2 && actionPayload.angebotsId === appState.angebotsId) {
        return {
          ...sectionState,
          wohnflaeche: actionPayload.wohnflaeche,
          hausart: actionPayload.hausart,
          staendigBewohnt: actionPayload.staendigBewohnt,
          gebaeudeStaendigBewohnt: actionPayload.staendigBewohnt,
          stockwerk: actionPayload.stockwerk,
          gebaeudeInnerhalbWohngebiet: true,
          plzFaktorErdb: actionPayload.plzFaktorErdb,
          plzFaktorGla: actionPayload.plzFaktorGla,
          plzFaktorHws: actionPayload.plzFaktorHws,
          selbstbehaltUeberschwemmung: actionPayload.selbstbehaltUeberschwemmung,
        };
      }
      return sectionState;
    }
    default:
      return sectionState;
  }
};
